import { API_URL } from './config';
import { Tournament } from '../../types/Tournament';

const getHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

export const tournamentsService = {
    // Public / Discovery
    listOpen: async (): Promise<Tournament[]> => {
        const res = await fetch(`${API_URL}/tournaments/open`);
        if (!res.ok) throw new Error("Failed to fetch open tournaments");
        return res.json();
    },
    getDetails: async (id: string): Promise<Tournament> => {
        const res = await fetch(`${API_URL}/tournaments/${id}`);
        if (!res.ok) throw new Error("Failed to fetch tournament details");
        return res.json();
    },
    join: async (id: string, data: { partner_id?: string, payment_method?: 'online' | 'on_site', payment_intent_id?: string }) => {
        const res = await fetch(`${API_URL}/tournaments/${id}/join`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || "Failed to join tournament");
        }
        return res.json();
    },

    // Manager
    create: async (data: Partial<Tournament>): Promise<Tournament> => {
        const res = await fetch(`${API_URL}/tournaments`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to create tournament");
        return res.json();
    },
    update: async (id: string, data: Partial<Tournament>): Promise<any> => {
        const res = await fetch(`${API_URL}/tournaments/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update tournament");
        return res.json();
    },
    updateStatus: async (id: string, status: Tournament['status']) => {
        // Backend hasn't implemented specific update status route separately? 
        // Logic shows it expects PUT /manager/tournaments/:id/status or similar?
        // In backend controller I saw updateStatus method, but no route in index.ts??
        // I need to ADD route for updateStatus in backend index.ts too if missing.
        const res = await fetch(`${API_URL}/tournaments/${id}/status`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ status })
        });
        if (!res.ok) throw new Error("Failed to update status");
        return res.json();
    },
    generate: async (id: string) => {
        const res = await fetch(`${API_URL}/tournaments/${id}/generate`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({})
        });
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || "Failed to generate matches");
        }
        return res.json();
    },
    reorganize: async (id: string, options?: { headRefParticipates: boolean }) => {
        const res = await fetch(`${API_URL}/tournaments/${id}/reorganize`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(options || {})
        });
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || "Failed to reorganize schedule");
        }
        return res.json();
    },
    listByClub: async (clubId: string): Promise<Tournament[]> => {
        // Backend route: /api/clubs/:id/tournaments
        // Make sure backend has this.
        const res = await fetch(`${API_URL}/clubs/${clubId}/tournaments`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch club tournaments");
        return res.json();
    },

    listPublicByClub: async (clubId: string): Promise<Tournament[]> => {
        const res = await fetch(`${API_URL}/public/clubs/${clubId}/tournaments`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch public club tournaments");
        return res.json();
    },
    delete: async (id: string) => {
        const res = await fetch(`${API_URL}/tournaments/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        if (!res.ok) throw new Error("Failed to delete tournament");
        return res.json();
    },

    async checkRegistration(tournamentId: string, userId: string): Promise<boolean> {
        try {
            const tournament = await this.getDetails(tournamentId);
            if (!tournament || !(tournament as any).participants) return false;
            return (tournament as any).participants.some((p: any) => p.id === userId || p.user_id === userId);
        } catch (e) {
            return false;
        }
    },

    async getMatches(id: string): Promise<any[]> {
        const res = await fetch(`${API_URL}/tournaments/${id}/matches`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch matches");
        return res.json();
    },

    listMy: async (): Promise<Tournament[]> => {
        const res = await fetch(`${API_URL}/tournaments/my`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch my tournaments");
        return res.json();
    },

    history: async (limit: number = 20, offset: number = 0): Promise<Tournament[]> => {
        const res = await fetch(`${API_URL}/tournaments/history?limit=${limit}&offset=${offset}`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch tournament history");
        return res.json();
    },

    listAll: async (): Promise<Tournament[]> => {
        const res = await fetch(`${API_URL}/admin/tournaments`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch all tournaments");
        return res.json();
    },

    // Referees
    inviteReferee: async (tournamentId: string, refereeUserId: string) => {
        const res = await fetch(`${API_URL}/tournaments/${tournamentId}/referees`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ referee_user_id: refereeUserId })
        });
        if (!res.ok) throw new Error("Failed to invite referee");
        return res.json();
    },
    getReferees: async (tournamentId: string) => {
        const res = await fetch(`${API_URL}/tournaments/${tournamentId}/referees`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch referees");
        return res.json();
    },
    respondInvitation: async (tournamentId: string, status: 'accepted' | 'rejected') => {
        const res = await fetch(`${API_URL}/tournaments/${tournamentId}/referees/respond`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ status })
        });
        if (!res.ok) throw new Error("Failed to respond to invitation");
        return res.json();
    },
    revokeInvitation: async (tournamentId: string, refereeUserId: string) => {
        const res = await fetch(`${API_URL}/tournaments/${tournamentId}/referees/${refereeUserId}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        if (!res.ok) throw new Error("Failed to revoke invitation");
        return res.json();
    },
    reorderReferees: async (tournamentId: string, refereeIds: string[]) => {
        const res = await fetch(`${API_URL}/tournaments/${tournamentId}/referees/reorder`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ refereeIds })
        });
        if (!res.ok) throw new Error("Failed to reorder referees");
        return res.json();
    },
    fillBots: async (id: string) => {
        const res = await fetch(`${API_URL}/tournaments/${id}/fill-bots`, {
            method: 'POST',
            headers: getHeaders()
        });
        if (!res.ok) {
            const err = await res.text();
            throw new Error(err || "Failed to fill with bots");
        }
        return res.json();
    }
};
